/**
 * Interpreters for `commandSequence` step `autoCheck` predicates.
 *
 * The maneuver HUD calls {@link evaluateAutoCheck} every sim tick (or once
 * per second — it's cheap) with the current {@link ChecklistContext} and a
 * step's `autoCheck` value. The function returns `true` once the predicate
 * is satisfied; once a step flips to "done" it should stay done for the rest
 * of the maneuver (the caller is responsible for the latch).
 *
 * Steps with `autoCheck: null` are user-driven — they're shown in the HUD
 * but never auto-complete.
 */

import type { AutoCheck } from "./gamification.types";

/**
 * Read-only view of the simulator state that's relevant to checklist steps.
 * Phase 3 wiring maps the simulation's internal types onto this view.
 */
export interface ChecklistContext {
  /**
   * Coarse anchor state used by the checklist. Maps from the simulator's
   * `DeployedAnchor.holdingState` plus the `state.anchorHoldingState` enum:
   * - `up`: anchor stowed (no deployed anchor).
   * - `ready`: windlass armed, ready to release (UI signal).
   * - `dropping`: anchor in flight, before touching the bottom.
   * - `down`: on the bottom but not yet set.
   * - `holding`: dug in, no drag.
   * - `dragging`: on the bottom but sliding.
   * - `weighing`: rode being hauled in.
   */
  readonly anchorState:
    | "up"
    | "ready"
    | "dropping"
    | "down"
    | "holding"
    | "dragging"
    | "weighing";
  /** Per-cleat line status from `PlayerMooringStations`. */
  readonly mooringLines: Readonly<
    Record<string, "attached" | "stowed" | "free">
  >;
  /** Player boat speed magnitude (m/s, horizontal). */
  readonly speedMs: number;
  /** Player boat heading relative to wind direction (deg, absolute, 0..180). */
  readonly headingVsWindDeg: number;
  /** Current anchor scope ratio (rode length / depth). NaN if no anchor down. */
  readonly anchorScopeRatio: number;
  /** Seconds since the anchor last entered `dragging`. ∞ if never. */
  readonly secondsSinceLastDrag: number;
  /** Fender counts per side. */
  readonly fendersDeployed: Readonly<{ port: number; starboard: number }>;
  /** Distance from the maneuver target (m). */
  readonly distanceFromTargetM: number;
  /** Crew role state — pluggable, depends on CrewCommandsStore vocabulary. */
  readonly crew: Readonly<Record<string, string>>;
  /** Absolute cumulative heading change accumulated since maneuver start (deg). */
  readonly cumulativeTurnDeg: number;
}

export function evaluateAutoCheck(
  check: AutoCheck | null,
  ctx: ChecklistContext,
): boolean {
  if (check === null) return false;
  switch (check.type) {
    case "anchorState":
      return ctx.anchorState === check.equals;
    case "mooringLine": {
      const status = ctx.mooringLines[check.cleat];
      return status === check.equals;
    }
    case "linesStowedAll":
      return Object.values(ctx.mooringLines).every(
        (s) => s === "stowed" || s === "free",
      );
    case "speedBelow":
      return ctx.speedMs < check.value;
    case "headingVsWind":
      return ctx.headingVsWindDeg <= check.maxDeg;
    case "anchorScopeRatio":
      return (
        Number.isFinite(ctx.anchorScopeRatio) &&
        ctx.anchorScopeRatio >= check.min
      );
    case "noDragForSec":
      return ctx.secondsSinceLastDrag >= check.value;
    case "fenderDeployed": {
      const needed = check.count ?? 1;
      return ctx.fendersDeployed[check.side] >= needed;
    }
    case "distanceFromTarget":
      return check.op === "<="
        ? ctx.distanceFromTargetM <= check.value
        : ctx.distanceFromTargetM >= check.value;
    case "crewState":
      return ctx.crew[check.role] === check.equals;
    case "turnedAtLeastDeg":
      return ctx.cumulativeTurnDeg >= check.minDeg;
    default: {
      // Exhaustiveness guard. If a new AutoCheck variant is added without a
      // matching case here, TypeScript will fail this assignment.
      const _exhaustive: never = check;
      void _exhaustive;
      return false;
    }
  }
}
